import {
  ActionIcon,
  Button,
  SegmentedControl,
  Select,
  TextInput,
  Tooltip,
  UnstyledButton,
} from '@mantine/core';
import {
  IconCalendarMonth,
  IconChartBar,
  IconChevronLeft,
  IconChevronRight,
  IconClipboardList,
  IconLink,
  IconPlus,
  IconRefreshDot,
  IconSearch,
  IconSettingsFilled,
} from '@tabler/icons-react';
import { type ReactNode, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../EmptyState.js';
import { useFitColumns } from '../../fit-columns.js';
import { formatDay } from '../../format.js';
import { Panel } from '../../Panel.js';
import { followLink, useShell } from '../../shell-context.js';
import { SortableTh, TablePager } from '../purchasing/table.js';
import classes from './Pms.module.css';
import work from './PmsWork.module.css';
import { PmsBadge, PmsPriorityBadge, RowsSkeleton } from './PmsWorkParts.js';
import {
  type CalendarTone,
  frequencyLabel,
  monthRange,
  paginate,
  plansIn,
  type Sort,
  searchWorkOrders,
  sortWorkOrders,
  upcoming,
  type WorkOrderSortKey,
  weekRange,
  workOrderDueDate,
} from './plan-view.js';
import { workOrderLate, workOrderStatus } from './status-tone.js';
import type { PlanItem, WorkOrderView } from './types.js';

/** Ay: `YYYY-MM`; gün `YYYY-MM-DD` (gemi yerel tarihi). */
function monthOf(day: string): string {
  return day.slice(0, 7);
}

function shiftMonth(month: string, delta: number): string {
  const [year, value] = month.split('-').map(Number);
  const at = new Date(Date.UTC(year ?? 2000, (value ?? 1) - 1 + delta, 1));
  return at.toISOString().slice(0, 7);
}

function shiftDay(day: string, delta: number): string {
  const at = new Date(`${day}T00:00:00Z`);
  at.setUTCDate(at.getUTCDate() + delta);
  return at.toISOString().slice(0, 10);
}

function useMonthLabel() {
  const { i18n } = useTranslation();
  const locale = i18n.resolvedLanguage === 'tr' ? 'tr-TR' : 'en-GB';
  return (month: string) =>
    new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(
      new Date(`${month}-01T00:00:00Z`),
    );
}

/**
 * Tablonun kaydırılabilir kutusu (WCAG 2.1.1: klavyeyle kayar). Dar kutuda `data-drop` ile
 * işaretli önemsiz sütunlar sırayla gizlenir; tablo yana kaymaz (`fit-columns.ts`).
 */
function TableBox({ label, children }: { label: string; children: ReactNode }) {
  const { t } = useTranslation();
  const ref = useFitColumns<HTMLElement>();
  return (
    <section
      ref={ref}
      className={classes.tableBox}
      aria-label={t('pms.equipment.tableOf', { name: label })}
      // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
      tabIndex={0}
    >
      {children}
    </section>
  );
}

function EmptyRow({ span, title, icon }: { span: number; title: string; icon?: ReactNode }) {
  return (
    <tr>
      <td colSpan={span} className={classes.emptyCell}>
        <EmptyState icon={icon} title={title} />
      </td>
    </tr>
  );
}

type PlanSortKey = 'date' | 'component' | 'workType' | 'frequency' | 'status' | 'assignedTo';

/**
 * Dar plan tablosunda gizlenme sırası (`fit-columns.ts`): önce sıklık (iş kartında ve Job
 * Library'de), sonra sorumlu, en son iş türü. Tarih, bileşen ve durum hiç gizlenmez.
 */
const PLAN_DROP: Partial<Record<PlanSortKey, number>> = {
  frequency: 1,
  assignedTo: 2,
  workType: 3,
};

export interface MaintenancePlanPanelProps {
  today: string;
  /** `undefined`: iş yok (boş durum); `null`: okunuyor. */
  plans?: readonly PlanItem[] | null;
  pageSize?: number;
  /** Satırın Job Card'ı (bileşen adı düğme olur). */
  onOpen?: (plan: PlanItem) => void;
  /** "Postponement pending" / "Interval change pending" rozetinden karar çekmecesi. */
  onDecide?: (plan: PlanItem, kind: 'postponement' | 'interval') => void;
  /** Satır menüsü ("…"). */
  rowActions?: (plan: PlanItem) => ReactNode;
  /** `false`: başlıklar sıralanmaz (Equipment List'teki dar panel; görselde sıralama oku yok). */
  sortable?: boolean;
  /**
   * Gecikmiş ya da ertelenmiş süzgeci seçili (ortak 2026-10-25; ADR 0108 Ek): tablo dönemle
   * sınırlanmaz; ay, hafta ve liste aynı işleri verir, tablonun üstünde not görünür.
   */
  allPeriods?: boolean;
  className?: string;
}

/**
 * "Maintenance Plan" paneli (`gemi-pms-ekipman.jpeg`): ay seçici, ‹ ›, Today, Month / Week / List
 * ve plan tablosu (Date, Component, Work Type, Frequency, Status, Assigned To). Vade ve durum plan
 * motorundan (K39); gecikmiş iş aralığın başından önce de listede kalır.
 */
export function MaintenancePlanPanel({
  today,
  plans,
  pageSize = 5,
  onOpen,
  onDecide,
  rowActions,
  sortable = true,
  allPeriods = false,
  className,
}: MaintenancePlanPanelProps) {
  const { t, i18n } = useTranslation();
  const k = (key: string) => t(`pms.equipment.plan.${key}`);
  const label = useMonthLabel();
  const [anchor, setAnchor] = useState(today);
  const [view, setView] = useState('month');
  const [sort, setSort] = useState<Sort<PlanSortKey>>({ key: 'date', dir: 'asc' });
  const [page, setPage] = useState(1);
  const month = monthOf(anchor);
  const months = useMemo(
    () =>
      Array.from({ length: 13 }, (_, i) => shiftMonth(monthOf(today), i - 6)).map((value) => ({
        value,
        label: label(value),
      })),
    [today, label],
  );
  const go = (day: string) => {
    setAnchor(day);
    setPage(1);
  };
  const moveTo = (nextMonth: string) =>
    go(nextMonth === monthOf(today) ? today : `${nextMonth}-01`);
  const visible = useMemo(() => {
    const range =
      view === 'list' || allPeriods
        ? {}
        : view === 'week'
          ? weekRange(anchor)
          : monthRange(monthOf(anchor));
    const list = plansIn(plans ?? [], range);
    if (sort.key === 'date' && sort.dir === 'asc') return list;
    const value = (p: PlanItem): string => {
      switch (sort.key) {
        case 'date':
          return p.due.dueOn ?? '9999';
        case 'component':
          return p.equipmentName ?? '';
        case 'workType':
          return p.workType;
        case 'frequency':
          return frequencyLabel(p.effectiveInterval, t, i18n.resolvedLanguage ?? 'en');
        case 'status':
          return p.status;
        case 'assignedTo':
          return p.responsibleRoleName ?? '';
      }
    };
    const sign = sort.dir === 'asc' ? 1 : -1;
    return [...list].sort(
      (a, b) => sign * value(a).localeCompare(value(b)) || a.code.localeCompare(b.code),
    );
  }, [plans, sort, view, anchor, allPeriods, t, i18n.resolvedLanguage]);
  const pageView = paginate(visible, page, pageSize);
  const columns: [PlanSortKey, string][] = [
    ['date', k('columns.date')],
    ['component', k('columns.component')],
    ['workType', k('columns.workType')],
    ['frequency', k('columns.frequency')],
    ['status', k('columns.status')],
    ['assignedTo', k('columns.assignedTo')],
  ];
  const language = i18n.resolvedLanguage ?? 'en';
  return (
    <Panel title={k('title')} className={[classes.fill, className].filter(Boolean).join(' ')}>
      <div className={classes.planBar}>
        <Select
          aria-label={k('period')}
          className={classes.monthSelect}
          data={
            months.some((m) => m.value === month)
              ? months
              : [...months, { value: month, label: label(month) }]
          }
          value={month}
          allowDeselect={false}
          onChange={(value) => {
            if (value) moveTo(value);
          }}
        />
        <span className={classes.stepper}>
          <ActionIcon
            variant="default"
            aria-label={view === 'week' ? k('previousWeek') : k('previous')}
            onClick={() =>
              view === 'week' ? go(shiftDay(anchor, -7)) : moveTo(shiftMonth(month, -1))
            }
          >
            <IconChevronLeft size={15} />
          </ActionIcon>
          <ActionIcon
            variant="default"
            aria-label={view === 'week' ? k('nextWeek') : k('next')}
            onClick={() =>
              view === 'week' ? go(shiftDay(anchor, 7)) : moveTo(shiftMonth(month, 1))
            }
          >
            <IconChevronRight size={15} />
          </ActionIcon>
        </span>
        <Button variant="default" size="xs" onClick={() => go(today)}>
          {k('today')}
        </Button>
        <SegmentedControl
          classNames={{
            root: classes.viewSwitch,
            indicator: classes.viewIndicator,
            label: classes.viewLabel,
          }}
          size="xs"
          value={view}
          onChange={(value) => {
            setView(value);
            setPage(1);
          }}
          data={[
            { value: 'month', label: k('month') },
            { value: 'week', label: k('week') },
            { value: 'list', label: k('list') },
          ]}
        />
      </div>
      {allPeriods ? (
        <p className={work.note} data-all-periods>
          {k('allPeriods')}
        </p>
      ) : null}
      <TableBox label={k('title')}>
        <table className={classes.table} data-compact data-cards>
          <thead>
            <tr>
              {columns.map(([key, name]) =>
                sortable ? (
                  <SortableTh
                    key={key}
                    column={key}
                    label={name}
                    sort={sort}
                    drop={PLAN_DROP[key]}
                    onSort={(next) => {
                      setSort(next);
                      setPage(1);
                    }}
                  />
                ) : (
                  <th key={key} scope="col" data-drop={PLAN_DROP[key]}>
                    {name}
                  </th>
                ),
              )}
              {rowActions ? (
                <th scope="col" className={classes.actionsHead}>
                  <span className={classes.srOnly}>
                    {t('pms.equipment.overview.columns.actions')}
                  </span>
                </th>
              ) : null}
            </tr>
          </thead>
          <tbody>
            {plans === null ? (
              <tr>
                <td colSpan={columns.length + (rowActions ? 1 : 0)} className={classes.emptyCell}>
                  <RowsSkeleton rows={4} />
                </td>
              </tr>
            ) : pageView.rows.length === 0 ? (
              <EmptyRow
                span={columns.length + (rowActions ? 1 : 0)}
                title={(plans ?? []).length === 0 ? k('empty') : k('emptyPeriod')}
              />
            ) : (
              pageView.rows.map((plan) => (
                <tr key={plan.jobId} data-overdue={plan.status === 'overdue' || undefined}>
                  <td data-label={k('columns.date')}>{formatDay(plan.due.dueOn)}</td>
                  <td data-label={k('columns.component')}>
                    {onOpen ? (
                      <UnstyledButton
                        className={work.linkButton}
                        onClick={() => onOpen(plan)}
                        aria-label={t('pms.work.plan.openJob', {
                          code: plan.code,
                          name: plan.equipmentName ?? plan.title,
                        })}
                      >
                        {plan.equipmentName ?? '—'}
                      </UnstyledButton>
                    ) : (
                      (plan.equipmentName ?? '—')
                    )}
                  </td>
                  <td data-label={k('columns.workType')} data-drop={PLAN_DROP.workType}>
                    {t(`pms.work.workTypes.${plan.workType}`, { defaultValue: plan.workType })}
                  </td>
                  <td data-label={k('columns.frequency')} data-drop={PLAN_DROP.frequency}>
                    {frequencyLabel(plan.effectiveInterval, t, language)}
                  </td>
                  <td data-label={k('columns.status')}>
                    <span className={work.badgeRow}>
                      <PmsBadge status={plan.status} />
                      {plan.postponementPending ? (
                        <PendingFlag
                          label={t('pms.work.plan.postponementPending')}
                          {...(onDecide ? { onClick: () => onDecide(plan, 'postponement') } : {})}
                        />
                      ) : null}
                      {plan.intervalChangePending ? (
                        <PendingFlag
                          label={t('pms.work.plan.intervalPending')}
                          {...(onDecide ? { onClick: () => onDecide(plan, 'interval') } : {})}
                        />
                      ) : null}
                    </span>
                  </td>
                  <td data-label={k('columns.assignedTo')} data-drop={PLAN_DROP.assignedTo}>
                    {plan.responsibleRoleName ?? '—'}
                  </td>
                  {rowActions ? <td className={work.actionsCell}>{rowActions(plan)}</td> : null}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </TableBox>
      {plans && visible.length > pageSize ? (
        <TablePager
          page={pageView.page}
          pages={pageView.pages}
          summary={t('pms.work.showing', {
            from: pageView.from,
            to: pageView.to,
            total: pageView.total,
          })}
          label={t('pms.work.pages', { name: k('title') })}
          onPage={setPage}
        />
      ) : null}
    </Panel>
  );
}

/** Bekleyen erteleme ya da aralık uzatması: karar verebilen için düğme. */
function PendingFlag({ label, onClick }: { label: string; onClick?: () => void }) {
  if (!onClick) {
    return (
      <span className={work.pendingFlag}>
        <IconRefreshDot size={12} aria-hidden />
        {label}
      </span>
    );
  }
  return (
    <UnstyledButton className={work.pendingFlag} data-action onClick={onClick}>
      <IconRefreshDot size={12} aria-hidden />
      {label}
    </UnstyledButton>
  );
}

/** "Upcoming Tasks (Next 7 Days)": vadesi bugünle yedi gün sonrası arasında olan işler. */
export function UpcomingTasksPanel({
  today,
  plans,
  className,
}: {
  today: string;
  plans?: readonly PlanItem[] | null;
  className?: string;
}) {
  const { t } = useTranslation();
  const k = (key: string) => t(`pms.equipment.upcoming.${key}`);
  const [page, setPage] = useState(1);
  const list = useMemo(() => upcoming(plans ?? [], today), [plans, today]);
  const pageView = paginate(list, page, 5);
  const columns = ['date', 'component', 'task', 'priority'] as const;
  return (
    <Panel
      title={k('title')}
      extra={<span className={classes.titleNote}>{k('window')}</span>}
      className={[classes.fill, classes.inlineExtra, className].filter(Boolean).join(' ')}
    >
      <TableBox label={k('title')}>
        <table className={classes.table} data-compact data-cards>
          <thead>
            <tr>
              {columns.map((c) => (
                <th key={c} scope="col" data-drop={c === 'task' ? 1 : undefined}>
                  {k(`columns.${c}`)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {plans === null ? (
              <tr>
                <td colSpan={columns.length} className={classes.emptyCell}>
                  <RowsSkeleton rows={4} />
                </td>
              </tr>
            ) : pageView.rows.length === 0 ? (
              <EmptyRow span={columns.length} title={k('empty')} />
            ) : (
              pageView.rows.map((plan) => (
                <tr key={plan.jobId}>
                  <td data-label={k('columns.date')}>{formatDay(plan.due.dueOn)}</td>
                  <td data-label={k('columns.component')}>{plan.equipmentName ?? '—'}</td>
                  {/* Dar kartta önce iş türü gizlenir (`fit-columns.ts`) */}
                  <td data-label={k('columns.task')} data-drop={1}>
                    {t(`pms.work.workTypes.${plan.workType}`, { defaultValue: plan.workType })}
                  </td>
                  <td data-label={k('columns.priority')}>
                    <PmsPriorityBadge priority={plan.priority} />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </TableBox>
      {list.length > 5 ? (
        <TablePager
          page={pageView.page}
          pages={pageView.pages}
          summary={t('pms.work.showing', {
            from: pageView.from,
            to: pageView.to,
            total: pageView.total,
          })}
          label={t('pms.work.pages', { name: k('title') })}
          onPage={setPage}
        />
      ) : null}
    </Panel>
  );
}

export interface WorkOrderPanelProps {
  scope: 'web' | 'ship';
  /** `undefined`: iş emri yok; `null`: okunuyor. */
  items?: readonly WorkOrderView[] | null;
  /** Gemide "+ New Work Order" ve "Generate Work Orders" (K9, K13). */
  canCreate?: boolean;
  onNew?: () => void;
  onGenerate?: () => void;
  onOpen?: (wo: WorkOrderView) => void;
  pageSize?: number;
  className?: string;
  /**
   * Vadesi geçmiş açık iş emrine "Overdue" rozeti (Work Orders ekranı; ADR 0029 Ek M7f F3)
   * [NR496 4.5.3c]. Ekipman listesindeki panel referans görüntüdeki gibi kalır (ADR 0019).
   */
  showOverdue?: boolean;
}

/**
 * "Work Order" paneli: arama, "+ New Work Order" ve "Generate Work Orders" (yalnız gemide; K9,
 * K13); WO No, Component, Task, Priority, Status, Due Date, Assigned To. Due Date planlı işte
 * plan motorundan, diğerlerinde hedef tarihten.
 */
export function WorkOrderPanel({
  scope,
  items,
  canCreate = true,
  onNew,
  onGenerate,
  onOpen,
  pageSize = 5,
  className,
  showOverdue = false,
}: WorkOrderPanelProps) {
  const { t } = useTranslation();
  const k = (key: string) => t(`pms.equipment.workOrders.${key}`);
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<Sort<WorkOrderSortKey>>({ key: 'woNo', dir: 'desc' });
  const [page, setPage] = useState(1);
  const list = useMemo(
    () => sortWorkOrders(searchWorkOrders(items ?? [], query), sort),
    [items, query, sort],
  );
  const pageView = paginate(list, page, pageSize);
  // Dar tabloda gizlenme sırası (`fit-columns.ts`): sorumlu, öncelik, iş türü
  const drop: Partial<Record<WorkOrderSortKey, number>> = { assignedTo: 1, priority: 2, task: 3 };
  const columns: [WorkOrderSortKey, string][] = [
    ['woNo', k('columns.no')],
    ['component', k('columns.component')],
    ['task', k('columns.task')],
    ['priority', k('columns.priority')],
    ['status', k('columns.status')],
    ['dueDate', k('columns.dueDate')],
    ['assignedTo', k('columns.assignedTo')],
  ];
  const ship = scope === 'ship' && canCreate;
  return (
    <Panel
      title={k('title')}
      icon={<IconClipboardList size={18} stroke={1.8} />}
      className={[classes.fill, className].filter(Boolean).join(' ')}
      extra={
        ship ? (
          <span className={work.panelActions}>
            {onGenerate ? (
              <Button size="xs" variant="default" onClick={onGenerate}>
                {k('generate')}
              </Button>
            ) : null}
            {onNew ? (
              <Button size="xs" leftSection={<IconPlus size={14} aria-hidden />} onClick={onNew}>
                {k('add')}
              </Button>
            ) : (
              <Tooltip label={k('soon')}>
                <span className={classes.disabledWrap}>
                  <Button size="xs" disabled leftSection={<IconPlus size={14} aria-hidden />}>
                    {k('add')}
                  </Button>
                </span>
              </Tooltip>
            )}
          </span>
        ) : undefined
      }
    >
      <TextInput
        className={classes.workSearch}
        size="xs"
        placeholder={k('search')}
        aria-label={k('searchLabel')}
        leftSection={<IconSearch size={13} aria-hidden />}
        value={query}
        disabled={!items || items.length === 0}
        onChange={(event) => {
          setQuery(event.currentTarget.value);
          setPage(1);
        }}
      />
      <TableBox label={k('title')}>
        <table className={classes.table} data-compact data-cards>
          <thead>
            <tr>
              {columns.map(([key, name]) => (
                <SortableTh
                  key={key}
                  column={key}
                  label={name}
                  sort={sort}
                  drop={drop[key]}
                  onSort={(next) => {
                    setSort(next);
                    setPage(1);
                  }}
                />
              ))}
            </tr>
          </thead>
          <tbody>
            {items === null ? (
              <tr>
                <td colSpan={columns.length} className={classes.emptyCell}>
                  <RowsSkeleton rows={4} />
                </td>
              </tr>
            ) : pageView.rows.length === 0 ? (
              <EmptyRow
                span={columns.length}
                title={(items ?? []).length === 0 ? k('empty') : k('noMatch')}
              />
            ) : (
              pageView.rows.map((wo) => (
                <tr key={wo.id}>
                  <td data-label={k('columns.no')}>
                    {onOpen ? (
                      <UnstyledButton className={work.linkButton} onClick={() => onOpen(wo)}>
                        {wo.woNo}
                      </UnstyledButton>
                    ) : (
                      wo.woNo
                    )}
                  </td>
                  <td data-label={k('columns.component')}>{wo.equipmentName ?? '—'}</td>
                  <td data-label={k('columns.task')} title={wo.title} data-drop={drop.task}>
                    {wo.workType
                      ? t(`pms.work.workTypes.${wo.workType}`, { defaultValue: wo.workType })
                      : wo.title}
                  </td>
                  <td data-label={k('columns.priority')} data-drop={drop.priority}>
                    <PmsPriorityBadge priority={wo.priority} />
                  </td>
                  <td data-label={k('columns.status')}>
                    {showOverdue && workOrderLate(wo) ? (
                      <span className={work.badgeRow}>
                        <PmsBadge status={workOrderStatus(wo)} />
                        <PmsBadge status="overdue" />
                      </span>
                    ) : (
                      <PmsBadge status={workOrderStatus(wo)} />
                    )}
                  </td>
                  <td data-label={k('columns.dueDate')}>{formatDay(workOrderDueDate(wo))}</td>
                  <td data-label={k('columns.assignedTo')} data-drop={drop.assignedTo}>
                    {wo.assignedToName ?? '—'}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </TableBox>
      {items && list.length > pageSize ? (
        <TablePager
          page={pageView.page}
          pages={pageView.pages}
          summary={t('pms.work.showing', {
            from: pageView.from,
            to: pageView.to,
            total: pageView.total,
          })}
          label={t('pms.work.pages', { name: k('title') })}
          onPage={setPage}
        />
      ) : null}
    </Panel>
  );
}

const LEGEND = [
  ['scheduled', 'info'],
  ['inProgress', 'warning'],
  ['overdue', 'danger'],
  ['completed', 'success'],
] as const;

const LEGEND_KEY: Readonly<Record<CalendarTone, string>> = {
  info: 'scheduled',
  warning: 'inProgress',
  danger: 'overdue',
  success: 'completed',
};

/**
 * "PMS Calendar": ayın ızgarası (pazartesiden başlar) ve Scheduled / In Progress / Overdue /
 * Completed noktaları; bugün çerçevelidir. Dar ekranda (< 768 px) ızgara yerine ayın listesi.
 */
export function PmsCalendar({
  today,
  marks,
  className,
}: {
  today: string;
  /** Ayın noktaları (`calendarMarks`); verilmezse nokta yok. */
  marks?: (month: string) => Map<string, CalendarTone[]>;
  className?: string;
}) {
  const { t, i18n } = useTranslation();
  const k = (key: string, values: Record<string, unknown> = {}) =>
    t(`pms.equipment.calendar.${key}`, values);
  const label = useMonthLabel();
  const [month, setMonth] = useState(() => monthOf(today));
  const locale = i18n.resolvedLanguage === 'tr' ? 'tr-TR' : 'en-GB';
  const weekdays = useMemo(() => {
    const format = new Intl.DateTimeFormat(locale, { weekday: 'short', timeZone: 'UTC' });
    // 2024-01-01 pazartesidir
    return Array.from({ length: 7 }, (_, i) =>
      format.format(new Date(Date.UTC(2024, 0, 1 + i))).replace('.', ''),
    );
  }, [locale]);
  const cells = useMemo(() => {
    const [year, value] = month.split('-').map(Number);
    const first = new Date(Date.UTC(year ?? 2000, (value ?? 1) - 1, 1));
    const days = new Date(Date.UTC(year ?? 2000, value ?? 1, 0)).getUTCDate();
    const lead = (first.getUTCDay() + 6) % 7;
    const list: { key: string; day: string | null }[] = [
      ...Array.from({ length: lead }, (_, n) => ({ key: `lead-${n}`, day: null })),
      ...Array.from({ length: days }, (_, n) => {
        const day = `${month}-${String(n + 1).padStart(2, '0')}`;
        return { key: day, day };
      }),
    ];
    const tail = (7 - (list.length % 7)) % 7;
    for (let n = 0; n < tail; n += 1) list.push({ key: `tail-${n}`, day: null });
    return list;
  }, [month]);
  const weeks: { key: string; day: string | null }[][] = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
  const dots = useMemo(() => marks?.(month) ?? new Map<string, CalendarTone[]>(), [marks, month]);
  const toneNames = (tones: readonly CalendarTone[]) =>
    tones.map((tone) => k(`legend.${LEGEND_KEY[tone]}`)).join(', ');
  return (
    <Panel title={k('title')} className={[classes.fill, className].filter(Boolean).join(' ')}>
      <div className={classes.calendar}>
        <div className={classes.calendarMain}>
          <div className={classes.calendarBar}>
            <ActionIcon
              variant="subtle"
              size="sm"
              aria-label={k('previous')}
              onClick={() => setMonth((m) => shiftMonth(m, -1))}
            >
              <IconChevronLeft size={15} />
            </ActionIcon>
            <span className={classes.calendarMonth} aria-live="polite">
              {label(month)}
            </span>
            <ActionIcon
              variant="subtle"
              size="sm"
              aria-label={k('next')}
              onClick={() => setMonth((m) => shiftMonth(m, 1))}
            >
              <IconChevronRight size={15} />
            </ActionIcon>
          </div>
          <table className={classes.calendarGrid}>
            <thead>
              <tr>
                {weekdays.map((day) => (
                  <th key={day} scope="col">
                    {day}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {weeks.map((week) => (
                <tr key={week[0]?.key}>
                  {week.map(({ key, day }) => {
                    if (!day) return <td key={key} aria-hidden />;
                    const tones = dots.get(day) ?? [];
                    return (
                      <td
                        key={key}
                        data-today={day === today || undefined}
                        aria-current={day === today ? 'date' : undefined}
                      >
                        <span className={classes.calendarDay}>{Number(day.slice(8))}</span>
                        {tones.length > 0 ? (
                          <span className={work.calendarDots}>
                            {tones.map((tone) => (
                              <span
                                key={tone}
                                className={classes.legendDot}
                                data-tone={tone}
                                aria-hidden
                              />
                            ))}
                            <span className={classes.srOnly}>{toneNames(tones)}</span>
                          </span>
                        ) : null}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
          {dots.size === 0 ? (
            <p className={classes.calendarList}>{k('empty')}</p>
          ) : (
            <ul className={[classes.calendarList, work.calendarItems].join(' ')}>
              {[...dots.entries()].map(([day, tones]) => (
                <li key={day}>
                  <span>{formatDay(day)}</span>
                  <span className={work.calendarItemTones}>
                    {tones.map((tone) => (
                      <span key={tone} className={classes.legendDot} data-tone={tone} aria-hidden />
                    ))}
                    {toneNames(tones)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <ul className={classes.legend}>
          {LEGEND.map(([key, tone]) => (
            <li key={key}>
              <span className={classes.legendDot} data-tone={tone} aria-hidden />
              {k(`legend.${key}`)}
            </li>
          ))}
        </ul>
      </div>
    </Panel>
  );
}

/**
 * "Quick Links": Maintenance Plan, Create Work Order (gemide; ofiste yerine "Work Orders"; K13),
 * Equipment List ve Reports (History ▸ Reports sekmesi).
 */
export function QuickLinks({ scope }: { scope: 'web' | 'ship' }) {
  const { t } = useTranslation();
  const shell = useShell();
  const k = (key: string) => t(`pms.equipment.quickLinks.${key}`);
  const links: { key: string; path: string; icon: ReactNode }[] = [
    { key: 'plan', path: '/pms/plan', icon: <IconCalendarMonth size={30} stroke={1.6} /> },
    scope === 'ship'
      ? {
          key: 'createWorkOrder',
          path: '/pms/work-orders?new=1',
          icon: <IconPlus size={30} stroke={1.8} />,
        }
      : { key: 'workOrders', path: '/pms/work-orders', icon: <IconClipboardList size={30} /> },
    { key: 'equipmentList', path: '/pms/equipment', icon: <IconSettingsFilled size={30} /> },
    { key: 'reports', path: '/pms/history?tab=reports', icon: <IconChartBar size={30} /> },
  ];
  return (
    <Panel title={k('title')} icon={<IconLink size={18} stroke={1.8} />} className={classes.fill}>
      <nav className={classes.quickLinks} aria-label={k('title')}>
        {links.map((link) => (
          <a
            key={link.key}
            href={link.path}
            className={classes.quickLink}
            onClick={(event) => {
              if (shell) followLink(event, link.path, shell.navigate);
            }}
          >
            <span className={classes.quickIcon} aria-hidden>
              {link.icon}
            </span>
            <span>{k(link.key)}</span>
          </a>
        ))}
      </nav>
    </Panel>
  );
}
